import React from 'react'
import { Link } from 'react-router-dom'
import { 
  Leaf, Calendar, Sparkles, Smartphone, ShieldCheck, 
  FileText, Activity, Apple, Video, CheckCircle2, Zap, ArrowRight, Star
} from 'lucide-react'

export default function SaasMarketingPage() {
  const primaryColor = '#4a613c' // Sage-600
  const accentColor = '#7d9668'  // Sage-400

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#fdfdf9] to-white text-[#4A5043] font-sans antialiased">
      
      {/* 1. HEADER / NAVIGATION */}
      <header className="sticky top-0 z-50 bg-[#fdfdf9]/80 backdrop-blur-md border-b border-gray-150/50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-10 h-10 rounded-full flex items-center justify-center shadow-inner bg-sage-50 text-sage-600">
              <Leaf className="w-5 h-5" />
            </div>
            <div>
              <h1 className="text-xl font-serif font-black text-gray-950">BellaBlue</h1>
              <p className="text-[10px] uppercase font-bold tracking-widest text-sage-600">B2B SaaS Workspace</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/admin/login" className="text-sm font-bold text-gray-600 hover:text-gray-900 px-3 py-2 transition-colors">
              Espace Praticien
            </Link>
            <Link 
              to="/admin/register" 
              className="px-5 py-2.5 rounded-xl text-white font-black text-sm transition-transform hover:-translate-y-0.5 shadow-md flex items-center gap-1.5"
              style={{ backgroundColor: primaryColor }}
            >
              Essai Gratuit
            </Link>
          </div>
        </div>
      </header>

      {/* 2. HERO SECTION */}
      <section className="relative overflow-hidden py-16 lg:py-24">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full blur-3xl opacity-20 -mr-64 -mt-32 pointer-events-none" style={{ backgroundColor: accentColor }}></div>
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 items-center">
          
          <div className="lg:col-span-6 space-y-6 text-center lg:text-left">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-sage-50 text-sage-700 border border-sage-200 rounded-full text-xs font-bold uppercase tracking-wider">
              <Sparkles className="w-3.5 h-3.5" /> Propulsé par l'IA Locale Gaston
            </span>
            
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-black leading-tight text-gray-950">
              Le premier logiciel de gestion <span style={{ color: primaryColor }}>tout-en-un</span> pour Diététiciennes et Diététiciens.
            </h2>
            
            <p className="text-lg text-gray-600 leading-relaxed max-w-xl mx-auto lg:mx-0">
              Libérez-vous de la charge administrative. Automatisez vos prises de rendez-vous, fidélisez vos patients, encaissez vos honoraires et pilotez votre cabinet médical avec sérénité.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4">
              <Link 
                to="/admin/register" 
                className="w-full sm:w-auto px-8 py-4 rounded-2xl text-white font-black text-lg transition-transform hover:-translate-y-1 shadow-lg text-center flex items-center justify-center gap-2"
                style={{ backgroundColor: primaryColor, boxShadow: `0 10px 25px -5px ${accentColor}40` }}
              >
                Essayer gratuitement <ArrowRight className="w-5 h-5"/>
              </Link>
              <span className="text-xs text-gray-400 font-bold">14 jours d'essai • Sans carte bancaire</span>
            </div>
          </div>

          <div className="lg:col-span-6 relative flex justify-center">
            {/* Visual Card Stack mock */}
            <div className="relative w-full max-w-md aspect-[4/3] bg-white rounded-3xl p-6 shadow-2xl border border-gray-100 flex flex-col justify-between">
              <div className="flex justify-between items-center pb-4 border-b border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-sage-100 text-sage-600 rounded-full flex items-center justify-center font-bold font-serif text-lg">ML</div>
                  <div>
                    <p className="font-bold text-gray-800">Marie Laurent</p>
                    <p className="text-xs text-gray-500">Consultation de suivi</p>
                  </div>
                </div>
                <span className="text-xs bg-green-50 text-green-700 px-3 py-1 rounded-full border border-green-200 font-bold uppercase">PAYÉ ✅</span>
              </div>
              <div className="py-4 space-y-2">
                <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">📝 Objectifs du jour</p>
                <div className="flex items-center gap-2 text-sm text-gray-700"><CheckCircle2 className="w-4 h-4 text-green-500"/> Boire 1.5L d'eau par jour</div>
                <div className="flex items-center gap-2 text-sm text-gray-700"><CheckCircle2 className="w-4 h-4 text-green-500"/> Réduire les sucres raffinés</div>
              </div>
              <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-100 p-3 rounded-2xl flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-purple-600 animate-pulse"/>
                  <span className="text-xs font-bold text-purple-900">Gaston IA a rédigé le compte-rendu.</span>
                </div>
                <span className="text-[10px] bg-purple-600 text-white font-bold px-2 py-0.5 rounded-full">Stream ON</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 3. BENTO GRID DE FONCTIONNALITÉS */}
      <section className="py-16 md:py-24 bg-gray-50/50 border-y border-gray-150/50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          
          <div className="text-center max-w-2xl mx-auto mb-16 space-y-3">
            <h3 className="font-serif text-3xl md:text-4xl font-black text-gray-950">Conçu pour la pratique libérale moderne</h3>
            <p className="text-gray-500 text-sm md:text-base">BellaBlue regroupe en une seule interface l'ensemble des modules nécessaires pour décoller et automatiser votre activité.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            
            {/* CARD 1 : PORTAIL PATIENT PWA */}
            <div className="bg-white p-8 rounded-3xl border border-gray-150 shadow-sm flex flex-col justify-between">
              <div className="w-12 h-12 rounded-2xl bg-sage-50 text-sage-600 flex items-center justify-center mb-6">
                <Smartphone className="w-6 h-6" />
              </div>
              <div className="space-y-2">
                <h4 className="font-bold text-gray-900 text-lg">Portail Patient Interactif (PWA)</h4>
                <p className="text-sm text-gray-500 leading-relaxed">
                  Pas de mot de passe à retenir. Vos patients s'identifient via un <strong>Magic Link (JWT)</strong> sécurisé et installent leur espace sur leur écran d'accueil d'un simple geste.
                </p>
              </div>
              <ul className="mt-4 pt-4 border-t border-gray-100 space-y-2 text-xs text-gray-600">
                <li className="flex items-center gap-2">✓ Carnet de repas (Scan OpenFoodFacts V3 & Photos)</li>
                <li className="flex items-center gap-2">✓ Courbe d'évolution du poids & des mensurations</li>
                <li className="flex items-center gap-2">✓ Suivi et validation en temps réel des objectifs</li>
              </ul>
            </div>

            {/* CARD 2 : GESTION DE CABINET */}
            <div className="bg-white p-8 rounded-3xl border border-gray-150 shadow-sm flex flex-col justify-between">
              <div className="w-12 h-12 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center mb-6">
                <Calendar className="w-6 h-6" />
              </div>
              <div className="space-y-2">
                <h4 className="font-bold text-gray-900 text-lg">Agenda & Consultations</h4>
                <p className="text-sm text-gray-500 leading-relaxed">
                  Un système d'agenda intelligent avec détection et gestion des conflits de chevauchement. Salles de téléconsultation (vidéo) intégrées de manière native.
                </p>
              </div>
              <ul className="mt-4 pt-4 border-t border-gray-100 space-y-2 text-xs text-gray-600">
                <li className="flex items-center gap-2">✓ White-Label PDF (Factures, Devis, Attestations)</li>
                <li className="flex items-center gap-2">✓ Prescription d'examens biologiques automatisée</li>
                <li className="flex items-center gap-2">✓ Gestion unifiée de la facturation Tiers-Payant</li>
              </ul>
            </div>

            {/* CARD 3 : GASTON IA */}
            <div className="bg-gradient-to-br from-purple-50 to-indigo-50 p-8 rounded-3xl border border-purple-200 shadow-sm flex flex-col justify-between">
              <div className="w-12 h-12 rounded-2xl bg-white text-purple-600 flex items-center justify-center mb-6 shadow-sm">
                <Sparkles className="w-6 h-6" />
              </div>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <h4 className="font-bold text-purple-950 text-lg">Assistant Gaston</h4>
                  <span className="text-[9px] bg-purple-200 text-purple-800 font-bold px-2 py-0.5 rounded-full">HDS COMPLIANT</span>
                </div>
                <p className="text-sm text-purple-900/80 leading-relaxed">
                  L'IA <b>locale</b> s'exécute de manière anonyme et souveraine. Gaston lit la timeline du patient (Repas, photos et sport) pour rédiger un résumé clinique complet en streaming. (Accès sur demande)
                </p>
              </div>
              <ul className="mt-4 pt-4 border-t border-purple-200/50 space-y-2 text-xs text-purple-900/70">
                <li className="flex items-center gap-2">✓ Synthèse d'anamnèse en streaming (effet machine)</li>
                <li className="flex items-center gap-2">✓ Rédaction de lettres à partir des notes des RDV</li>
                <li className="flex items-center gap-2">✓ Analyse croisée antécédents vs symptômes réels</li>
              </ul>
            </div>

            {/* CARD 4 : BIBLIOTHÈQUE DE DOCUMENTS */}
            <div className="bg-white p-8 rounded-3xl border border-gray-150 shadow-sm flex flex-col justify-between">
              <div className="w-12 h-12 rounded-2xl bg-orange-50 text-orange-600 flex items-center justify-center mb-6">
                <FileText className="w-6 h-6" />
              </div>
              <div className="space-y-2">
                <h4 className="font-bold text-gray-900 text-lg">Bibliothèque & Masques</h4>
                <p className="text-sm text-gray-500 leading-relaxed">
                  Créez des "Masques de saisie" pour structurer vos bilans. Les réponses sont stockées au format JSON et injectées dynamiquement dans vos documents.
                </p>
              </div>
              <ul className="mt-4 pt-4 border-t border-gray-100 space-y-2 text-xs text-gray-600">
                <li className="flex items-center gap-2">✓ Questionnaire d'accueil envoyé automatiquement</li>
                <li className="flex items-center gap-2">✓ Modèles de courriers, certificats et consentements</li>
                <li className="flex items-center gap-2">✓ Partage sécurisé de dossier aux confrères (lien éphémère)</li>
              </ul>
            </div>

            {/* CARD 5 : CRM & TASKS */}
            <div className="bg-white p-8 rounded-3xl border border-gray-150 shadow-sm flex flex-col justify-between">
              <div className="w-12 h-12 rounded-2xl bg-yellow-50 text-yellow-600 flex items-center justify-center mb-6">
                <Activity className="w-6 h-6" />
              </div>
              <div className="space-y-2">
                <h4 className="font-bold text-gray-900 text-lg">CRM & Relances Automatiques</h4>
                <p className="text-sm text-gray-500 leading-relaxed">
                  Un moteur de relance automatique (SMS & Emails) pour les RDV ou les patients inactifs et un outil de campagne marketing ciblé ("Affiche-moi les patients diabétiques").
                </p>
              </div>
              <ul className="mt-4 pt-4 border-t border-gray-100 space-y-2 text-xs text-gray-600">
                <li className="flex items-center gap-2">✓ Rappels SMS 48h/24h gratuits (via passerelle Android)</li>
                <li className="flex items-center gap-2">✓ Campagnes d'information collectives SMS & Emails</li>
                <li className="flex items-center gap-2">✓ Pense-Bête clinique intelligent synchronisé</li>
              </ul>
            </div>

            {/* CARD 6 : SECURITY */}
            <div className="bg-white p-8 rounded-3xl border border-gray-150 shadow-sm flex flex-col justify-between">
              <div className="w-12 h-12 rounded-2xl bg-green-50 text-green-600 flex items-center justify-center mb-6">
                <ShieldCheck className="w-6 h-6" />
              </div>
              <div className="space-y-2">
                <h4 className="font-bold text-gray-900 text-lg">Sécurité & Chiffrement HDS</h4>
                <p className="text-sm text-gray-500 leading-relaxed">
                  L'intégrité de vos données de santé est au cœur de nos préoccupations. Les secrets, credentials et clés API de votre cabinet sont chiffrés de manière asymétrique au repos.
                </p>
              </div>
              <ul className="mt-4 pt-4 border-t border-gray-100 space-y-2 text-xs text-gray-600">
                <li className="flex items-center gap-2">✓ Chiffrement fort des clés API et informations sensibles</li>
                <li className="flex items-center gap-2">✓ Audit Trail (traçabilité HDS complète des accès support)</li>
                <li className="flex items-center gap-2">✓ Archivage et export global RGPD en un clic (ZIP)</li>
              </ul>
            </div>

          </div>
        </div>
      </section>

      {/* 4. TARIFICATION CLAIRE & TRANSPARENTE */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center max-w-3xl">
          <h3 className="font-serif text-3xl md:text-4xl font-black text-gray-950 mb-3">Une tarification simple, sans mauvaise surprise.</h3>
          <p className="text-gray-500 text-sm md:text-base mb-12">Profitez de l'intégralité de la plateforme BellaBlue, sans aucun bridage fonctionnel.</p>
          
          <div className="grid sm:grid-cols-2 gap-6 max-w-2xl mx-auto">
            
            {/* PLAN MENSUEL */}
            <div className="bg-white rounded-3xl p-8 border border-gray-200 shadow-sm hover:shadow-md transition-shadow text-left flex flex-col justify-between">
              <div>
                <p className="text-sm font-bold text-gray-400 uppercase tracking-widest">Plan Mensuel</p>
                <div className="flex items-baseline gap-1.5 mt-4">
                  <span className="text-5xl font-black text-gray-950">19€</span>
                  <span className="text-sm font-bold text-gray-500">/ mois</span>
                </div>
                <p className="text-xs text-gray-400 mt-2">Sans aucun engagement. Résiliation en un clic.</p>
                
                <ul className="mt-8 space-y-3 text-sm text-gray-600">
                  <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-green-500"/> Patients et consultations illimités</li>
                  <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-green-500"/> Rappels SMS illimités gratuits</li>
                  <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-green-500"/> 1 Go de stockage médical inclus</li>
                </ul>
              </div>
              <Link 
                to="/admin/register" 
                className="w-full btn bg-gray-100 hover:bg-gray-200 text-gray-800 py-3 rounded-xl font-bold mt-8 text-center"
              >
                Démarrer l'essai gratuit
              </Link>
            </div>

            {/* PLAN ANNUEL */}
            <div className="bg-white rounded-3xl p-8 border-2 border-sage-400 shadow-lg text-left flex flex-col justify-between relative overflow-hidden">
              <div className="absolute -top-1 -right-1 bg-orange-500 text-white text-[10px] font-black px-3 py-1 rounded-bl-xl uppercase tracking-wider">
                2 MOIS OFFERTS 🎁
              </div>
              <div>
                <p className="text-sm font-bold text-sage-600 uppercase tracking-widest">Plan Annuel</p>
                <div className="flex items-baseline gap-1.5 mt-4">
                  <span className="text-5xl font-black text-gray-950">192€</span>
                  <span className="text-sm font-bold text-gray-500">/ an</span>
                </div>
                <p className="text-xs text-gray-400 mt-2">Équivaut à seulement 16€/mois.</p>
                
                <ul className="mt-8 space-y-3 text-sm text-gray-600">
                  <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-sage-600"/> Tout le contenu du plan standard</li>
                  <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-sage-600"/> Support prioritaire par e-mail</li>
                  <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-sage-600"/> Mises à jour & correctifs inclus</li>
                </ul>
              </div>
              <Link 
                to="/admin/register" 
                className="w-full btn bg-sage-600 hover:bg-sage-700 text-white py-3 rounded-xl font-bold mt-8 text-center shadow-md"
              >
                Démarrer l'essai gratuit
              </Link>
            </div>

          </div>
        </div>
      </section>

      {/* 5. TÉMOIGNAGE SÉCURISÉ & DE CONFIANCE */}
      <section className="py-16 md:py-24 bg-sage-50/30 border-t border-gray-150/50">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="flex justify-center gap-1 mb-6 text-amber-400">
            {[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5 fill-current" />)}
          </div>
          <blockquote className="text-xl md:text-2xl font-serif text-gray-800 italic leading-relaxed mb-6">
            "BellaBlue a radicalement changé ma pratique libérale. Mes patients adorent leur carnet de vie via leur portail et ne l'oublient jamais, le rappel SMS fait chuter mes rendez-vous non honorés, et l'assistant Gaston me fait gagner un temps précieux lors de la rédaction de mes lettres de suivi clinique."
          </blockquote>
          <p className="font-bold text-gray-900">Laëtitia</p>
          <p className="text-xs text-gray-500 font-bold uppercase tracking-wider mt-0.5">Diététicienne-Nutritionniste Collaboratrice</p>
        </div>
      </section>

      {/* 6. PIED DE PAGE */}
      <footer className="border-t border-gray-200 py-12 bg-white">
        <div className="max-w-7xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-sage-50 text-sage-600 flex items-center justify-center shadow-inner">
              <Leaf className="w-4 h-4" />
            </div>
            <span className="font-serif font-bold text-gray-900 text-lg">BellaBlue SaaS</span>
          </div>
          <p className="text-xs text-gray-400">
            © {new Date().getFullYear()} BellaBlue. Tous droits réservés.
          </p>
        </div>
      </footer>

    </div>
  )
}